export type UsersDataTableOrgItem = {
  id?: number;
  name: string;
};

export type OrgChipStyles = {
  color: string;
  backgroundColor: string;
};

export function orgChipPaletteKey(org: UsersDataTableOrgItem): string {
  if (org.id != null) {
    return `id:${org.id}:name:${org.name}`;
  }
  return `name:${org.name}`;
}

/** Signed 32-bit wrap (same as historical `x | 0` in djb2), without `| 0`. */
function toInt32(n: number): number {
  const m = 4294967296;
  let u = Math.trunc(n);
  u = ((u % m) + m) % m;
  return u >= 2147483648 ? u - m : u;
}

/** djb2-style hash over full Unicode code points (via for...of). */
function stableHash32(input: string): number {
  let h = 5381;
  for (const c of input) {
    const cp = c.codePointAt(0) ?? 0;
    h = toInt32((h << 5) + h + cp);
  }
  return Math.abs(h);
}

/** Mix to avalanche bits (reduces collisions when mapping to HSL). */
function mix32(x: number): number {
  let v = x >>> 0;
  v ^= v >>> 16;
  v = Math.imul(v, 0x7feb352d) >>> 0;
  v ^= v >>> 15;
  v = Math.imul(v, 0x846ca68b) >>> 0;
  v ^= v >>> 16;
  return v >>> 0;
}

/**
 * Single 32-bit seed per org key: XOR two salted hashes so similar strings
 * diverge; mix32 spreads bits across hue/sat/light buckets (~10⁵ combinations).
 */
function orgColorSeed(key: string): number {
  const a = stableHash32(key) >>> 0;
  const b = stableHash32(`\u0000org\u0000${key}`) >>> 0;
  return mix32(a ^ b);
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  h = ((h % 360) + 360) % 360;
  s /= 100;
  l /= 100;
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ];
}

/**
 * Solid label color plus a light translucent fill of the same hue
 * (aligned with filled chip tokens: foreground + ~15% alpha background).
 * Key includes id + name when id exists; `orgColorSeed` spreads keys across
 * hue (golden-ratio mix), saturation, and lightness for distinct chips.
 */
export function getStableOrgChipStyles(
  org: UsersDataTableOrgItem,
): OrgChipStyles {
  const s = orgColorSeed(orgChipPaletteKey(org));
  const hue = (Math.imul(s, 0x9e3779b9) >>> 0) % 360;
  const sat = 40 + ((s >>> 7) % 23);
  const light = 36 + ((s >>> 16) % 15);
  const [r, g, b] = hslToRgb(hue, sat, light);
  return {
    color: `rgb(${r}, ${g}, ${b})`,
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.15)`,
  };
}
